import { Link } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { SideStoryPlacementChoice, SideStoryPlacementSuggestionSet } from "@/features/book-assembly/book-assembly.types";
import { sideStoryPlacementKinds } from "@/features/book-assembly/book-assembly.types";

type ChapterOption = {
  id: string;
  label: string;
};

type SideStoryArrangementStepProps = {
  aiSuggestionsLocked: boolean;
  chapterOptions: ChapterOption[];
  choices: SideStoryPlacementChoice[];
  onApplySuggestionSet: () => void;
  onIncludedChange: (storyId: string, included: boolean) => void;
  onPlacementChange: (
    storyId: string,
    placementKind: SideStoryPlacementChoice["placementKind"],
    anchorChapterId: string | null,
  ) => void;
  onRequestSuggestionSet: () => void;
  suggestionSet: SideStoryPlacementSuggestionSet | null;
  suggestionStatus: "error" | "idle" | "loading" | "ready";
};

const NONE_VALUE = "__none__";

export function SideStoryArrangementStep({
  aiSuggestionsLocked,
  chapterOptions,
  choices,
  onApplySuggestionSet,
  onIncludedChange,
  onPlacementChange,
  onRequestSuggestionSet,
  suggestionSet,
  suggestionStatus,
}: SideStoryArrangementStepProps) {
  const { t } = useTranslation();
  const suggestionsByStoryId = new Map(
    suggestionSet?.suggestions.map((suggestion) => [suggestion.storyId, suggestion]) ?? [],
  );
  const includedCount = choices.filter((choice) => choice.included).length;

  return (
    <section className="space-y-6">
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">
            {t("bookAssembly.steps.sideStories.eyebrow")}
          </p>
          <h2 className="text-3xl text-foreground">
            {t("bookAssembly.steps.sideStories.title")}
          </h2>
          <p className="max-w-3xl text-sm leading-7 text-muted-foreground">
            {t("bookAssembly.steps.sideStories.body")}
          </p>
        </div>

        <div className="flex flex-col gap-3 border-y border-primary/20 py-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="space-y-1">
            <p className="text-sm font-semibold text-foreground">
              {t("bookAssembly.steps.sideStories.aiTitle")}
            </p>
            <p className="max-w-2xl text-sm text-muted-foreground">
              {aiSuggestionsLocked
                ? t("bookAssembly.steps.sideStories.aiLocked")
                : t("bookAssembly.steps.sideStories.aiBody")}
            </p>
          </div>
          <Button
            disabled={includedCount === 0 || suggestionStatus === "loading" || aiSuggestionsLocked}
            onClick={onRequestSuggestionSet}
            type="button"
            variant="outline"
          >
            <Sparkles className="h-4 w-4" />
            {suggestionStatus === "loading"
              ? t("bookAssembly.steps.sideStories.requesting")
              : t("bookAssembly.steps.sideStories.request")}
          </Button>
        </div>

        {choices.length === 0 ? (
          <div className="border-y border-dashed border-ink/15 py-5 text-sm text-muted-foreground">
            {t("bookAssembly.steps.sideStories.empty")}
          </div>
        ) : null}

        {suggestionSet ? (
          <div className="space-y-3 border-y border-primary/20 py-5">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="space-y-1">
                <p className="text-sm font-semibold text-foreground">
                  {t("bookAssembly.steps.sideStories.suggestionTitle", {
                    creditCost: suggestionSet.creditCost,
                    creditsRemaining: suggestionSet.creditsRemaining,
                  })}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t("bookAssembly.steps.sideStories.suggestionBody")}
                </p>
              </div>
              <Button onClick={onApplySuggestionSet} type="button">
                {t("bookAssembly.steps.sideStories.applyAll")}
              </Button>
            </div>
          </div>
        ) : null}

        <div className="divide-y divide-ink/10 border-y border-ink/10">
          {choices.map((choice) => {
            const suggestion = suggestionsByStoryId.get(choice.storyId);

            return (
              <div
                className="space-y-4 py-5"
                key={choice.storyId}
              >
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="outline">{t("bookAssembly.common.sideStory")}</Badge>
                      {choice.included ? (
                        <Badge variant="default">{t("bookAssembly.common.included")}</Badge>
                      ) : (
                        <Badge variant="secondary">{t("bookAssembly.common.excluded")}</Badge>
                      )}
                    </div>
                    <div>
                      <p className="text-lg font-semibold text-foreground">{choice.title}</p>
                      <p className="max-w-3xl text-sm leading-6 text-muted-foreground">
                        {choice.summary}
                      </p>
                    </div>
                  </div>

                  <label className="inline-flex items-center gap-3 text-sm font-medium text-foreground">
                    <input
                      checked={choice.included}
                      className="h-4 w-4 rounded border-input accent-primary"
                      onChange={(event) =>
                        onIncludedChange(choice.storyId, event.currentTarget.checked)
                      }
                      type="checkbox"
                    />
                    {t("bookAssembly.steps.sideStories.include")}
                  </label>
                </div>

                {choice.included ? (
                  <div className="grid gap-4 lg:grid-cols-[14rem_minmax(0,1fr)] lg:items-end">
                    <div className="space-y-2">
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                        {t("bookAssembly.steps.sideStories.placementLabel")}
                      </p>
                      <Select
                        onValueChange={(value) =>
                          onPlacementChange(
                            choice.storyId,
                            value as SideStoryPlacementChoice["placementKind"],
                            value === "appendix" ? null : choice.anchorChapterId,
                          )
                        }
                        value={choice.placementKind}
                      >
                        <SelectTrigger aria-label={`${choice.title} reading position`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {sideStoryPlacementKinds.map((placementKind) => (
                            <SelectItem key={placementKind} value={placementKind}>
                              {t(`bookAssembly.placementKinds.${placementKind}`)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                        {t("bookAssembly.steps.sideStories.anchorLabel")}
                      </p>
                      <Select
                        disabled={choice.placementKind === "appendix"}
                        onValueChange={(value) =>
                          onPlacementChange(
                            choice.storyId,
                            choice.placementKind,
                            value === NONE_VALUE ? null : value,
                          )
                        }
                        value={choice.anchorChapterId ?? NONE_VALUE}
                      >
                        <SelectTrigger aria-label={`${choice.title} anchor chapter`}>
                          <SelectValue
                            placeholder={
                              choice.placementKind === "appendix"
                                ? t("bookAssembly.steps.sideStories.appendixAnchor")
                                : t("bookAssembly.steps.sideStories.anchorPlaceholder")
                            }
                          />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NONE_VALUE}>
                            {t("bookAssembly.steps.sideStories.anchorNone")}
                          </SelectItem>
                          {chapterOptions.map((chapter) => (
                            <SelectItem key={chapter.id} value={chapter.id}>
                              {chapter.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ) : null}

                {suggestion ? (
                  <div className="space-y-3 border-l-2 border-primary/30 bg-primary/5 py-4 pl-4 pr-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="default">{t("bookAssembly.common.aiSuggestion")}</Badge>
                      <Badge variant="outline">
                        {t(`bookAssembly.placementKinds.${suggestion.suggestedPlacementKind}`)}
                      </Badge>
                    </div>
                    <p className="text-sm leading-7 text-foreground">{suggestion.rationaleText}</p>
                    <div className="space-y-2">
                      {suggestion.citations.map((citation) => (
                        <div
                          className="border-t border-primary/15 py-3 text-sm first:border-t-0"
                          key={`${citation.sourceKind}:${citation.sourceRecordId}`}
                        >
                          <p className="font-medium text-foreground">
                            {citation.openHref ? (
                              <Link className="underline-offset-4 hover:underline" to={citation.openHref}>
                                {citation.sourceLabel}
                              </Link>
                            ) : (
                              citation.sourceLabel
                            )}
                          </p>
                          <p className="text-muted-foreground">{citation.secondaryText}</p>
                          <p className="mt-2 text-muted-foreground">{citation.reason}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
    </section>
  );
}
